/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import {
    IconAlignCenter,
    IconAlignLeft,
    IconAlignRight,
    IconFile,
    IconFileOff,
    IconH1,
    IconH2,
    IconH3,
    IconH4,
    IconIndentDecrease,
    IconIndentIncrease,
    IconLink,
    IconList,
    IconListNumbers,
    IconMinus,
    // IconPalette,
    IconPilcrow,
    IconTablePlus,
    IconUnlink,
    IconTextCaption,
} from "@tabler/icons-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { ToolbarButton } from "./toolbar-button/toolbar-button";
import { ToolbarButtonGroup } from "./toolbar-button/toolbar-button-group";
import { ClearFormattingButton } from "./clear-formatting-button";
import { AddColumnsButton } from "./add-columns-button";
import { MarkButtons } from "./mark-buttons";
import { TableButtons } from "./table-buttons";
import { ToolbarRow } from "./toolbar-row";
import { AddImageButton } from "cms/rich-text/simplicity-composer-tiptap/toolbar/add-image-button";

const _Toolbar = () => {
    
    const { t } = useTranslation();

    return (
        <div className='composer-toolbar'>
            <ToolbarRow>
                <ToolbarButtonGroup>
                    <ToolbarButton
                        command='toggleHeading'
                        args={[{ level: 1 }]}
                        icon={IconH1}
                        activeAttribute='heading'
                        tooltip={t("Editor.h1")}
                    />
                    <ToolbarButton
                        command='toggleHeading'
                        args={[{ level: 2 }]}
                        icon={IconH2}
                        activeAttribute='heading'
                        tooltip={t("Editor.h2")}
                    />
                    <ToolbarButton
                        command='toggleHeading'
                        args={[{ level: 3 }]}
                        icon={IconH3}
                        activeAttribute='heading'
                        tooltip={t("Editor.h3")}
                    />
                    <ToolbarButton
                        command='toggleHeading'
                        args={[{ level: 4 }]}
                        icon={IconH4}
                        activeAttribute='heading'
                        tooltip={t("Editor.h4")}
                    />
                    <ToolbarButton
                        command='setHorizontalRule'
                        icon={IconMinus}
                        // activeAttribute='hr'
                        tooltip={t("Editor.hr")}
                    />
                    <ToolbarButton
                        command='setParagraph'
                        icon={IconPilcrow}
                        activeAttribute='paragraph'
                        tooltip={t("Editor.paragraph")}
                    />

                    {/* Here we set an attribute on the node */}
                    <ToolbarButton
                        command='updateAttributes'
                        args={["paragraph", { class: "intro" } ]}
                        icon={IconTextCaption}
                        activeAttribute={{ class: "intro" }}
                        tooltip='Add an Intro styled paragraph'
                    />
                
                </ToolbarButtonGroup>

                <MarkButtons />

                <ToolbarButtonGroup>
                    <ToolbarButton
                        command='setTextAlign'
                        args={["left"]}
                        icon={IconAlignLeft}
                        activeAttribute={{ textAlign: "left" }}
                        tooltip={t("Editor.leftAlign")}
                    />
                    <ToolbarButton
                        command='setTextAlign'
                        args={["center"]}
                        icon={IconAlignCenter}
                        activeAttribute={{ textAlign: "center" }}
                        tooltip={t("Editor.centerAlign")}
                    />
                    <ToolbarButton
                        command='setTextAlign'
                        args={["right"]}
                        icon={IconAlignRight}
                        activeAttribute={{ textAlign: "right" }}
                        tooltip={t("Editor.rightAlign")}
                    />
                </ToolbarButtonGroup>

                <ToolbarButtonGroup>
                    
                    <ClearFormattingButton />
                    
                    <ToolbarButton
                        command='toggleLink'
                        icon={IconLink}
                        activeIcon={IconUnlink}
                        tooltip={t("Editor.link")}
                        activeTooltip={t("Editor.unlink")}
                        activeAttribute='link'
                    />
                    <ToolbarButton
                        // style={{border: '1px solid red', color: 'red'}}
                        command='toggleDocLink'
                        icon={IconFile}
                        activeIcon={IconFileOff}
                        tooltip={t("Editor.documentAdd")}
                        activeTooltip={t("Editor.documentRemove")}
                        activeAttribute='doclink'
                    />
                    
                    <AddImageButton />
                    
                    <ToolbarButton
                        command='insertTable'
                        icon={IconTablePlus}
                        activeAttribute='table'
                        args={[{ rows: 2, cols: 2, withHeaderRow: true }]}
                        tooltip={t("Editor.addTable")}
                    />
                   
                </ToolbarButtonGroup>

                <ToolbarButtonGroup>
                    <ToolbarButton
                        command='toggleBulletList'
                        icon={IconList}
                        activeAttribute='bulletList'
                        tooltip={t("Editor.bulletList")}
                    />
                    <ToolbarButton
                        command='toggleOrderedList'
                        icon={IconListNumbers}
                        activeAttribute='orderedList'
                        tooltip={t("Editor.orderedList")}
                    />
                    <ToolbarButton
                        command='sinkListItem'
                        icon={IconIndentIncrease}
                        args={["listItem"]}
                        tooltip={t("Editor.indent")}
                    />
                    <ToolbarButton
                        command='liftListItem'
                        icon={IconIndentDecrease}
                        args={["listItem"]}
                        tooltip={t("Editor.unindent")}
                    />
                </ToolbarButtonGroup>
                
                
                <ToolbarButtonGroup>
                    <AddColumnsButton />
                </ToolbarButtonGroup>
                
            </ToolbarRow>

            <TableButtons />
        </div>
    );
};

export const Toolbar = memo(_Toolbar);
Toolbar.displayName = "Toolbar";
